import { useState } from 'react'
import { Button, Chip, IconButton, Input, Spinner, Switch, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { PiX } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'

import { logger } from '../utils/helpers/logger'
import { updateExperienceDetails } from '../utils/helpers/upload.helpers'
import { validateEmail } from '../utils/helpers/validate.helpers'
import { MAX_NOTIFY_EMAILS } from '../utils/constants/notifications.constants'
import { useGeneralToast } from '../hooks/toast'
import { useSessionContext } from '../contexts/SessionContext'
import { AgamiExperience } from '../types/supabase'

interface ExperienceNotificationsBlockProps {
  experience: AgamiExperience
  darkMode?: boolean
  onSaved: () => void
}

/**
 * The notifications block of the sharing panel (04.1): one switch for the
 * experience's notifications, plus additional addresses for people who are not
 * Agami accounts (the concrete case: an external project supervisor).
 *
 * Always visible, even with no delegates (decided 2026-08-07): the natural order
 * is to configure BEFORE sharing, and hiding the block would make the feature
 * invisible exactly to whoever is about to share for the first time.
 *
 * The "will be notified anyway" list keeps the additional field understandable:
 * without it, the first thing an owner would add is their own address. It holds
 * the owner alone, like the sender (04.2): suggesters are not natural recipients
 * (decided 2026-10-20), so the UI never promises a recipient the send skips.
 */
const ExperienceNotificationsBlock: React.FC<ExperienceNotificationsBlockProps> = ({
  experience,
  darkMode,
  onSaved,
}) => {
  const { t } = useTranslation()
  const { user } = useSessionContext()
  const { success: toastSuccess, error: toastError } = useGeneralToast()

  const [notifyEnabled, setNotifyEnabled] = useState(Boolean(experience.notify_enabled))
  const [emails, setEmails] = useState<string[]>(experience.notify_emails ?? [])
  const [newEmail, setNewEmail] = useState('')
  const [inputError, setInputError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // What the database holds, moved forward on every successful save: the save
  // action exists only while the form differs from it, so "is it saved?" is
  // answered by the button being there or not.
  const [saved, setSaved] = useState({
    enabled: Boolean(experience.notify_enabled),
    emails: experience.notify_emails ?? [],
  })

  // Order-sensitive on purpose: removing an address and adding it back is a
  // change the owner made, and saving it is harmless.
  const isDirty =
    notifyEnabled !== saved.enabled ||
    emails.length !== saved.emails.length ||
    emails.some((email, index) => email !== saved.emails[index])

  const naturalRecipients = [user?.email ?? ''].filter(Boolean)

  const addEmail = () => {
    const candidate = newEmail.trim()
    if (!candidate) return

    if (emails.length >= MAX_NOTIFY_EMAILS) {
      setInputError(t('sharing.notifications.errors.tooMany', { max: MAX_NOTIFY_EMAILS }))
      return
    }

    if (!validateEmail(candidate)) {
      setInputError(t('sharing.notifications.errors.invalidEmail', { email: candidate }))
      return
    }

    // A duplicate is rejected, not silently deduplicated: it is a typo the owner
    // wants to see. Case-insensitive, like the dedup the sender applies (04.2).
    if (emails.some((e) => e.toLowerCase() === candidate.toLowerCase())) {
      setInputError(t('sharing.notifications.errors.duplicateEmail', { email: candidate }))
      return
    }

    setEmails((prev) => [...prev, candidate])
    setNewEmail('')
    setInputError(null)
  }

  const removeEmail = (email: string) => {
    setEmails((prev) => prev.filter((e) => e !== email))
    setInputError(null)
  }

  // Back to what the database holds, including a half-typed address and its error.
  const discardChanges = () => {
    setNotifyEnabled(saved.enabled)
    setEmails(saved.emails)
    setNewEmail('')
    setInputError(null)
  }

  const saveNotifications = async () => {
    setSaving(true)

    const { error } = await updateExperienceDetails({
      expNewData: { id: experience.id, notify_enabled: notifyEnabled, notify_emails: emails },
    })

    if (error) {
      logger.error(error, 'SaveExperienceNotifications')
      toastError(t('sharing.notifications.errors.saveError'))
      setSaving(false)
      return
    }

    toastSuccess(t('sharing.notifications.saveSuccess'))
    setSaved({ enabled: notifyEnabled, emails })
    setSaving(false)
    onSaved()
  }

  return (
    <div className="mt-6" data-testid="notifications-block">
      <Switch
        color={(darkMode ? 'accent' : 'primary') as colors}
        id="experience_notifications_switch"
        label={t('sharing.notifications.switchLabel')}
        checked={notifyEnabled}
        onChange={(e) => setNotifyEnabled(e.currentTarget.checked)}
        disabled={saving}
        data-testid="notifications-switch"
      />

      <Typography variant="small" className="mt-2 font-normal">
        {t('sharing.notifications.explanation')}
      </Typography>

      {notifyEnabled && (
        <div className="mt-4 flex flex-col gap-3">
          <div data-testid="notifications-natural-recipients">
            <Typography variant="small" className="font-bold">
              {t('sharing.notifications.naturalRecipients')}
            </Typography>
            <div className="mt-1 flex flex-wrap gap-1">
              {naturalRecipients.map((email) => (
                <Chip
                  key={email}
                  className="inline-block normal-case"
                  variant="ghost"
                  value={email}
                  data-testid="notifications-natural-recipient"
                />
              ))}
            </div>
          </div>

          <Typography variant="small" className="font-normal">
            {t('sharing.notifications.additionalHint')}
          </Typography>

          {emails.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {emails.map((email) => (
                <Chip
                  key={email}
                  className="inline-flex items-center normal-case"
                  data-testid="notifications-email-chip"
                  value={
                    <span className="flex items-center gap-1">
                      {email}
                      <IconButton
                        size="sm"
                        variant="text"
                        className="h-4 w-4 min-w-0"
                        color="white"
                        disabled={saving}
                        data-testid="notifications-remove-email-btn"
                        onClick={() => removeEmail(email)}
                      >
                        <PiX />
                      </IconButton>
                    </span>
                  }
                />
              ))}
            </div>
          )}

          <div className="flex items-start gap-2">
            <div className="flex-1">
              <Input
                data-testid="notifications-email-input"
                value={newEmail}
                label={t('sharing.notifications.additionalLabel')}
                size="md"
                type="email"
                onChange={(e) => {
                  setNewEmail(e.target.value)
                  setInputError(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addEmail()
                  }
                }}
                disabled={saving}
                error={!!inputError}
                {...(darkMode && { color: 'white' })}
              />
              {!!inputError && (
                <Typography variant="small" color="red" data-testid="notifications-email-error">
                  {inputError}
                </Typography>
              )}
            </div>
            <Button
              size="sm"
              variant="outlined"
              color={darkMode ? 'white' : ('primary' as colors)}
              // h-10 is the md Input's height: the two sit side by side.
              className="h-10 whitespace-nowrap"
              disabled={saving}
              data-testid="notifications-add-email-btn"
              onClick={addEmail}
            >
              {t('sharing.notifications.add')}
            </Button>
          </div>
        </div>
      )}

      {isDirty && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            className="flex justify-center"
            disabled={saving}
            data-testid="notifications-save-btn"
            onClick={saveNotifications}
          >
            {saving ? <Spinner className="h-4 w-4" /> : t('common.save')}
          </Button>
          <Button
            size="sm"
            variant="outlined"
            color={darkMode ? 'white' : ('primary' as colors)}
            disabled={saving}
            data-testid="notifications-cancel-btn"
            onClick={discardChanges}
          >
            {t('common.cancel')}
          </Button>
          <Typography variant="small" className="font-normal italic" data-testid="notifications-unsaved">
            {t('sharing.notifications.unsavedChanges')}
          </Typography>
        </div>
      )}
    </div>
  )
}

export default ExperienceNotificationsBlock
